/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Why a returned bid was not used, as one vocabulary** (frame `0.1` slices `5a`/`5b`, `#1110`
 * `#1109` `#1342`).
 *
 * Todd, 2026-09-16: what matters is the **count and rate** of rejected ads by several dimensions, not
 * the individual ad — so a rejection is one metric event with a reason, and every place that rejects
 * speaks these words. A reason is added here, once, rather than spelled at the site that rejects.
 *
 * The reasons have **different dispositions**, and that is the point of naming them apart
 * (`DEMAND_REQUEST_SHAPE_DESIGN.md` RS5d-ii):
 *
 * | Reason | Scope | Disposition |
 * | --- | --- | --- |
 * | `below-source-floor` | the source's own contract: its floor, **or what we pay it** (`#1352`) | **discarded** at ingestion — no later break can accept it |
 * | `ad-restriction` | **the source's own** restrictions (`#1401`) | **discarded** at ingestion — its contract binds its ads in every break |
 * | | **this break's** restrictions, every selected source's combined | **retained, unselected** — the next break's may differ |
 * | `unexpected-currency` | the response: priced in a currency we did not ask for (`#1318` item 6) | **discarded** at ingestion — we hold no exchange rates, so the price cannot be compared with anything |
 * | `below-selection-floor` | **this break's** floor, stated by the publisher on the ad request (`#1103`, slice `5d`) | **retained, unselected** — the next break may carry a lower floor |
 *
 * **Imports only `demand-source-cost`**, itself shared and import-free, because the console aliases this
 * for the labels (`shared-modules.spec.ts`).
 */

import { floorPriceOf, netValueOf, type DemandSourceCost, type DemandSourcePrice } from './demand-source-cost';

export type BidRejectionReason =
  | 'below-source-floor'
  | 'ad-restriction'
  | 'unexpected-currency'
  | 'below-selection-floor';

/** Every reason, in the order a report lists them. */
export const BID_REJECTION_REASONS: readonly BidRejectionReason[] = Object.freeze([
  'below-source-floor',
  'ad-restriction',
  'unexpected-currency',
  'below-selection-floor',
]);

/**
 * The sentence an operator reads for each reason. It names what happened to the ad, not the code
 * that decided it (`#1045`).
 */
export const BID_REJECTION_LABELS: Readonly<Record<BidRejectionReason, string>> = Object.freeze({
  'below-source-floor': "Priced below the source's floor or its cost",
  'ad-restriction': "Outside the source's or the break's ad restrictions",
  'unexpected-currency': 'Priced in a currency we did not ask for',
  'below-selection-floor': "Priced below the break's floor",
});

/** How many returned ads one reason rejected. */
export interface BidRejectionCount {
  reason: BidRejectionReason;
  count: number;
}

/**
 * What a driver's result declares about the ads it did not hand back.
 *
 * Declared on the contract, not attached by cast (`#1309`): a caller can forward only what it can
 * name. `adsReturned` in reporting means **what the partner returned**, so a caller adds these back
 * to the ads it received before reporting it.
 */
export interface BidRejections {
  rejectedAds?: BidRejectionCount[];
}

/** Total rejected across reasons. */
export function totalRejected(rejections: readonly BidRejectionCount[] | undefined): number {
  return (rejections ?? []).reduce((sum, entry) => sum + entry.count, 0);
}

/** What {@link partitionBySourceFloor} needs to know about the source. */
export interface SourceFloorTerms {
  /**
   * What the auction uses as the source's gross value, **resolved** (`effectivePrice`) — its
   * contractual floor or its fixed price, or `undefined` when none is in force. Required rather than
   * optional, so a caller cannot skip the floor by leaving it out.
   */
  price: DemandSourcePrice | undefined;
  /** Whether the source is required, resolved (`effectiveRequired`). */
  required: boolean | undefined;
  /**
   * What we pay the source, resolved (`effectiveCost`), or `undefined` when none is in force.
   * Required rather than optional, so a caller cannot skip the loss check by leaving it out.
   */
  cost: DemandSourceCost | undefined;
}

/**
 * **A source's floor, applied to its own bids** (`#1109`, RS5d F1).
 *
 * Four rules, each ratified:
 * - it tests against the **contractual minimum** (a `floor` price), never the floor a round asked for — a
 *   strategy asking \$20 of a partner whose minimum is \$15 does not make a \$17 bid unsellable;
 * - a **fixed price never discards** — the recorded price is the agreed one, so nothing is compared;
 * - a bid with **no price is kept and not counted** (Todd, 2026-09-16) — nothing shows it is under the
 *   floor, and discarding it would empty every VAST source that sends no `<Pricing>`;
 * - a **required source is never floored** (Todd, 2026-09-16, from the rehearsal) — its ads are a
 *   direct-sold commitment somebody is expecting, agreed at whatever price they carry. Restrictions
 *   still bind it (`#1110`): *what may be shown* is not a price.
 *
 * And a fifth (Todd, 2026-09-17, `#1352`): **an ad worth less than nothing to us is never served** —
 * one whose net value, after what we pay the source, is negative. It has the same disposition as a
 * floor, because it is the same kind of fact: the source's own terms, fixed for every break. So it
 * shares the reason rather than inventing one (Todd: *"rare enough that it's not worth inventing a new
 * reason"*). Two differences from the floor, both deliberate:
 * - a **fixed CPM does not exempt it** — an agreed price below the agreed cost is still a loss;
 * - it reads the **cost**, never a `netValue` already on the ad. It first read a mapped one, but
 *   `ad.netValue` is not a response target (`#1320`, slice `2a`): bidBrain computes it from the bid and
 *   the cost, and a response cannot replace that calculation.
 *
 * A required source stays exempt from both.
 *
 * It only ever sees one source's ads, which is what keeps it from becoming a pod-level floor.
 */
export function partitionBySourceFloor<T extends { bid?: number | null }>(
  ads: readonly T[],
  terms: SourceFloorTerms,
): { kept: T[]; rejected: number } {
  if (terms.required === true) return { kept: [...ads], rejected: 0 };
  // A fixed price never floors (see above), which `floorPriceOf` already answers: it returns the
  // value only for a `floor`. A zero floor rejects nothing, so it is not applied.
  const stated = floorPriceOf(terms.price);
  const floor = stated !== undefined && stated > 0 ? stated : undefined;
  const kept: T[] = [];
  let rejected = 0;
  for (const ad of ads) {
    const price = ad.bid;
    if (typeof price !== 'number' || !Number.isFinite(price)) {
      kept.push(ad);
      continue;
    }
    if ((floor !== undefined && price < floor) || netValueOf(price, terms.cost) < 0) {
      rejected += 1;
    } else {
      kept.push(ad);
    }
  }
  return { kept, rejected };
}

/**
 * **The currency every OpenRTB request asks to be priced in** — `cur` on the request and `bidfloorcur`
 * on each impression, and what `unexpected-currency` compares a response against. Here rather than in
 * the driver so the console's diagnosis names the same value the driver asked for.
 */
export const REQUESTED_BID_CURRENCY = 'USD';

/**
 * **OpenRTB's no-bid reason codes, by name** (`BidResponse.nbr`, OpenRTB 2.5/2.6 §5.24; `#1318` item 6).
 *
 * Beside the rejection reasons because it answers the neighbouring question — *why no bid came back*
 * rather than *why a bid was not used* — and because this module is already shared with the console,
 * so the driver's log and the test diagnosis name a code the same way. Only the codes the standard
 * defines are named; `500+` is the exchange's own, and anything else is reported as a number rather
 * than guessed at (*never invent a third party's capability*).
 */
export const ORTB_NO_BID_REASONS: Readonly<Record<number, string>> = Object.freeze({
  0: 'unknown error',
  1: 'technical error',
  2: 'invalid request',
  3: 'known web spider',
  4: 'suspected non-human traffic',
  5: 'cloud, data center or proxy IP',
  6: 'unsupported device',
  7: 'blocked publisher or site',
  8: 'unmatched user',
  9: 'daily reader cap met',
  10: 'daily domain cap met',
});

/** `"blocked publisher or site (7)"`, `"exchange-specific reason 512"`, or `"reason code 42"`. */
export function describeNoBidReason(code: number): string {
  const named = ORTB_NO_BID_REASONS[code];
  if (named) return `${named} (${code})`;
  return code >= 500 ? `exchange-specific reason ${code}` : `reason code ${code}`;
}
